import React, { useState } from 'react';
import { Logo } from './Logo';
import { BetaBadge } from './BetaBadge';
import { Menu } from 'lucide-react';
import { HabitSessionWidget } from '../HabitSessionWidget';
import { HabitSessionManager } from '../HabitSessionManager';
import { RoutineSessionWidget } from '../RoutineSessionWidget';
import { RoutineNextWidget } from '../RoutineNextWidget';
import { RoutineSessionManager } from '../RoutineSessionManager';
import { FocusSessionWidget } from '../FocusSessionWidget';
import { useCurrentView } from '../../hooks/useCurrentView';
import { useHabitSessionStore } from '../../stores/habitSessionStore';
import { useRoutineSessionStore } from '../../stores/routineSessionStore';
import { useFocusSessionStore } from '../../stores/focusSessionStore';
import { useHabitStore } from '../../stores/habitStore';
import { useNavDrawer } from '../../stores/navDrawerStore';
import { useCoinRewardStore } from '../../stores/coinRewardStore';
import { useProgressDisplayStore } from '../../stores/progressDisplayStore';
import { HeaderReward } from './HeaderReward';
import { HeaderNotifications } from './HeaderNotifications';
import { useSidebarStore, selectSidebarCollapsed } from './Navigation';

export function Header() {
  const [expandedSession, setExpandedSession] = useState(false);
  const { currentView } = useCurrentView();
  const { activeSession, stopSession } = useHabitSessionStore();
  const routineRuntime = useRoutineSessionStore(s => s.runtime);
  const focusActive = useFocusSessionStore(s => s.active);
  const { habits } = useHabitStore();
  // Focus widget shows only when a focus session is active AND we've left Focus.
  const showFocusWidget = focusActive && currentView !== 'current';
  const { open: openDrawer } = useNavDrawer();
  const coinDot = useCoinRewardStore((s) => s.pendingDot);
  const clearCoinDot = useCoinRewardStore((s) => s.clearDot);
  const { mode: progressMode, headerWidgetPosition } = useProgressDisplayStore();
  const isSidebarCollapsed = useSidebarStore(selectSidebarCollapsed);

  const currentHabit = activeSession
    ? habits.find(h => h.id === activeSession.habitId)
    : null;

  // Any session (or several) can surface a header widget; they share the slot.
  const hasWidget = !!activeSession || !!routineRuntime || showFocusWidget;
  const sessionWidget = hasWidget ? (
    <div className="flex flex-wrap items-center gap-2">
      {activeSession && (
        <HabitSessionWidget
          onExpand={() => setExpandedSession(true)}
          onStop={async () => {
            await stopSession();
          }}
        />
      )}
      {routineRuntime && <RoutineSessionWidget />}
      {routineRuntime && <RoutineNextWidget />}
      {showFocusWidget && <FocusSessionWidget />}
    </div>
  ) : null;

  return (
    <>
      <header className="bg-card border-b border-border sticky top-0 z-40 safe-area-pt">
        <div className="px-4 pt-1 pb-3 lg:py-4">
          <div className="relative flex items-center justify-between gap-4">
            {/* Left: hamburger (mobile) opens the side-nav drawer; logo (desktop) */}
            <button
              type="button"
              onClick={() => {
                clearCoinDot();
                openDrawer();
              }}
              /* ⚠️ On a phone the wallet is NOT on screen — the rail is desktop
                 only, the bottom bar has no wallet and the header shows no
                 balance, so it lives behind this button. An earned coin flies
                 here and leaves the dot below, which is the phone's version of
                 a balance that ticked up. */
              data-coin-target="menu"
              data-guide="nav.menu"
              aria-label={coinDot ? 'Open navigation menu — new coin in your wallet' : 'Open navigation menu'}
              aria-haspopup="dialog"
              className="lg:hidden relative flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Menu className="w-6 h-6" />
              {coinDot && (
                <span
                  aria-hidden="true"
                  className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-card"
                />
              )}
            </button>
            {/* ⭐ The Beta badge hangs below the wordmark, out of flow — the row keeps its height. */}
            <div className="relative hidden lg:block">
              <Logo />
              <BetaBadge />
            </div>
            {/* Middle slot (normal flow): the session widget when positioned
                left/right, otherwise (mobile) the level-progress line bar. */}
            {hasWidget && headerWidgetPosition !== 'center' ? (
              <div className={`flex-1 flex min-w-0 ${headerWidgetPosition === 'right' ? 'justify-end' : 'justify-start'}`}>
                <div className="min-w-0 max-w-md">{sessionWidget}</div>
              </div>
            ) : !hasWidget && progressMode !== 'none' ? (
              <div className="flex-1 max-w-md lg:hidden">
                <HeaderReward mode={progressMode} />
              </div>
            ) : null}

            {/* Active session, centered over the CONTENT CANVAS — an overlay
                offset by the desktop rail width so its centre lines up with the
                main content (not the full-width header). On mobile there's no
                rail, so it centres on the viewport. */}
            {hasWidget && headerWidgetPosition === 'center' && (
              <div
                className={`pointer-events-none absolute inset-0 flex items-center justify-center ${
                  isSidebarCollapsed ? 'lg:pl-[5rem]' : 'lg:pl-[13rem]'
                }`}
              >
                <div className="pointer-events-auto max-w-[60%]">{sessionWidget}</div>
              </div>
            )}

            <div className="flex items-center gap-2 ml-auto">
              {/* The avatar IS the global notification center: badge = unread
                  count, click opens the panel (the level lives on the rail /
                  progress surfaces). Mobile keeps the progress ring modes. */}
              <HeaderNotifications
                showMobileRing={progressMode === 'ring' || progressMode === 'both'}
              />
            </div>
          </div>
        </div>
      </header>

      {expandedSession && currentHabit && (
        <HabitSessionManager
          habit={currentHabit}
          onClose={() => setExpandedSession(false)}
        />
      )}

      {/* Active routine run — globally mounted so it survives navigation; shows
          full-screen when expanded, hidden (still ticking) when minimized. */}
      <RoutineSessionManager />
    </>
  );
}
